import { useState, useEffect, useMemo } from 'react';
import { Textfit } from 'react-textfit';
import styles from './BankingStatistics.module.css';

// ─── 상수 ────────────────────────────────────────────────────────────────────

const LINE_MAX_CAPACITY = 1300;   // Line 1개당 최대 용량
const BOX_MAX_CAPACITY  = 100;    // Box 1개당 최대 용량
const HIGH_VALUE_THRESHOLD = 97;  // 위험 임계값(%)
const MIN_BAR_WIDTH_PERCENT = 3;  // 최소 바 너비(시각적 가독성)

// [2026-09-09] ARB(Assay Ready Bank) 추가 — Master/Working 2탭 → 3탭
//   flag 는 Banking_Info.Master 값이다. '1' Master · '0' Working · '2' ARB
const STOCK_TYPE = {
    MASTER:  { key: 'master',  label: 'Master Stock',  flag: '1', color: '#E78E38' },
    WORKING: { key: 'working', label: 'Working Stock', flag: '0', color: 'green'   },
    ARB:     { key: 'arb',     label: 'ARB',           flag: '2', color: '#2F80ED' },
};

// ─── 유틸 ────────────────────────────────────────────────────────────────────

const toPercent = (count, max) => (count / max) * 100;

const clampBarWidth = (percent) => Math.max(percent, MIN_BAR_WIDTH_PERCENT);

const buildBoxUrl = (vessel, line, box) =>
    `https://banking.gradiantbio.com/${vessel}/${line}/${box}`;

// ─── 서브 컴포넌트 ───────────────────────────────────────────────────────────

const PercentageBar = ({ percent, color, onClick }) => {
    const isHigh = percent > HIGH_VALUE_THRESHOLD;
    return (
        <div
            className={styles.percentageBarContainer}
            onClick={onClick}
            style={onClick ? { cursor: 'pointer' } : undefined}
        >
            <div
                className={`${styles.percentageBar} ${isHigh ? styles.highValuePercentage : ''}`}
                style={{
                    width: `${clampBarWidth(percent)}%`,
                    backgroundColor: color,
                }}
            />
            <span className={styles.percentageValue}>{percent.toFixed(0)}%</span>
        </div>
    );
};

const LineRow = ({ lineStatus, isSelected, color, onSelect }) => {
    const percent = toPercent(lineStatus.count, LINE_MAX_CAPACITY);
    return (
        <div
            className={`${styles.lineContainer} ${isSelected ? styles.selectedLineContainer : ''}`}
            onClick={() => onSelect(lineStatus.Line)}
        >
            <div className={`${styles.lineName} ${isSelected ? styles.selectedLine : ''}`}>
                <Textfit mode="single" max={13}>{lineStatus.Line}</Textfit>
            </div>
            <PercentageBar percent={percent} color={color} />
        </div>
    );
};

const BoxRow = ({ boxStatus, color, onNavigate }) => {
    const percent = toPercent(boxStatus.count, BOX_MAX_CAPACITY);
    const handleClick = () => onNavigate(boxStatus.Box);
    return (
        <div className={styles.boxDetail} onClick={handleClick} style={{ cursor: 'pointer' }}>
            <div className={styles.boxName}>{boxStatus.Box}</div>
            <PercentageBar percent={percent} color={color} />
        </div>
    );
};

const StockTab = ({ label, isActive, onClick }) => (
    <div
        className={isActive ? styles.boxNameFront : styles.boxNameBack}
        onClick={onClick}
    >
        {label}
    </div>
);

// ─── 메인 컴포넌트 ───────────────────────────────────────────────────────────

function BankingStatistics({ vessel }) {
    const [stockType, setStockType] = useState(STOCK_TYPE.MASTER);
    const [statusByBox, setStatusByBox] = useState([]);
    const [statusByLine, setStatusByLine] = useState([]);
    const [selectedLine, setSelectedLine] = useState(null);

    // 두 API 동시 호출
    useEffect(() => {
        const reqOption = {
            method: 'post',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ vesselNo: vessel }),
        };

        Promise.all([
            fetch('/api/getOrganoidStatusByVesselNo', reqOption).then(r => r.json()),
            fetch('/api/getOrganoidStatusByVesselNoToLine', reqOption).then(r => r.json()),
        ]).then(([boxData, lineData]) => {
            setStatusByBox(boxData);
            setStatusByLine(lineData);
        });
    }, [vessel]);

    // 탭 전환 시 선택된 Line 초기화
    const handleTabChange = (nextType) => {
        setStockType(nextType);
        setSelectedLine(null);
    };

    // 현재 탭에 해당하는 Line / Box 목록 (불필요한 재계산 방지)
    const filteredLines = useMemo(
        () => statusByLine.filter(s => s.Master === stockType.flag),
        [statusByLine, stockType.flag]
    );

    const filteredBoxes = useMemo(
        () => statusByBox.filter(b => b.Line === selectedLine && b.Master === stockType.flag),
        [statusByBox, selectedLine, stockType.flag]
    );

    const navigateToBox = (boxName) => {
        if (!selectedLine) return; // 가드: Line 미선택 시 이동 차단
        window.location.href = buildBoxUrl(vessel, selectedLine, boxName);
    };

    return (
        <div className={styles.outerBox}>
            {/* 탭 */}
            <div className={styles.masterWorkingTab}>
                <StockTab
                    label={STOCK_TYPE.MASTER.label}
                    isActive={stockType.key === STOCK_TYPE.MASTER.key}
                    onClick={() => handleTabChange(STOCK_TYPE.MASTER)}
                />
                <StockTab
                    label={STOCK_TYPE.WORKING.label}
                    isActive={stockType.key === STOCK_TYPE.WORKING.key}
                    onClick={() => handleTabChange(STOCK_TYPE.WORKING)}
                />
                <StockTab
                    label={STOCK_TYPE.ARB.label}
                    isActive={stockType.key === STOCK_TYPE.ARB.key}
                    onClick={() => handleTabChange(STOCK_TYPE.ARB)}
                />
                <div className={styles.leftBox} />
            </div>

            <div className={styles.masterWorkingBottomBox}>
                <div className={styles.vesselNo}>Storage-{vessel}</div>

                {/* Line 목록 */}
                <div>
                    <div className={styles.StockCategory}>{stockType.label}</div>
                    {filteredLines.map(lineStatus => (
                        <LineRow
                            key={lineStatus.Line}
                            lineStatus={lineStatus}
                            isSelected={selectedLine === lineStatus.Line}
                            color={stockType.color}
                            onSelect={setSelectedLine}
                        />
                    ))}
                </div>

                {/* 선택된 Line의 Box 목록 */}
                <div className={styles.boxDataSquare}>
                    {selectedLine && (
                        <div className={styles.stockSection}>
                            <div className={styles.stockHeader}>{stockType.label}</div>
                            {filteredBoxes.map(boxStatus => (
                                <BoxRow
                                    key={`${boxStatus.Line}-${boxStatus.Box}`}
                                    boxStatus={boxStatus}
                                    color={stockType.color}
                                    onNavigate={navigateToBox}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default BankingStatistics;